import { PaginationProps } from "antd/lib/pagination/Pagination";
import {
  RowSelectionType,
  SortOrder,
  TableRowSelection,
} from "antd/lib/table/interface";
import { Reducer, useCallback, useEffect, useMemo, useReducer } from "react";
import { Model, ModelFilter } from "react3l-common";
import { Observable } from "rxjs";
import * as yup from "yup";
import { FilterAction, FilterActionEnum } from "./filter-service";
import { listService } from "./list-service";
import { ValidationField } from "./validation-service";

type KeyType = string | number;
export interface ModalState {
  visible?: boolean;
  loadControl?: boolean;
}

export interface ModalAction {
  type: ModalActionEnum;
}

export interface ContentTableState<
  TMapping extends Model,
  TMapper extends Model = any
> {
  mappingList?: TMapping[]; // for content table
  mapperList?: TMapper[]; // for content modal table
}
export enum ContentTableActionEnum {
  SET_LIST_SELECTION,
  SINGLE_DELETE,
  BULK_DELETE,
}
export interface ContentTableAction<
  TMapping extends Model,
  TMapper extends Model = any
> {
  type: ContentTableActionEnum;
  listName?: string;
  mappingList?: TMapping[];
  mapperList?: TMapper[];
  data?: any[];
}

export enum ModalActionEnum {
  OPEN_MODAL,
  CLOSE_MODAL,
  END_LOAD_CONTROL,
  INIT_SEARCH,
}
function modalTableReducer(state: ModalState, action: ModalAction) {
  switch (action.type) {
    case ModalActionEnum.OPEN_MODAL: {
      return {
        ...state,
        visible: true,
        loadControl: true,
      };
    }
    case ModalActionEnum.CLOSE_MODAL: {
      return {
        ...state,
        visible: false,
        loadControl: false,
      };
    }
    case ModalActionEnum.END_LOAD_CONTROL: {
      return {
        ...state,
        loadControl: false,
      };
    }
    case ModalActionEnum.INIT_SEARCH: {
      return {
        ...state,
        loadControl: true,
      };
    }
  }
}

function contentTableReducer<TMapping extends Model, TMapper extends Model>(
  state: ContentTableState<TMapping, TMapper>,
  action: ContentTableAction<TMapping, TMapper>
) {
  switch (action.type) {
    case ContentTableActionEnum.SINGLE_DELETE: {
      return {
        ...state,
        mappingList: action.mappingList,
      };
    }
    case ContentTableActionEnum.BULK_DELETE: {
      return {
        ...state,
        mappingList: [],
      };
    }
    case ContentTableActionEnum.SET_LIST_SELECTION: {
      return {
        ...state,
        [action.listName]: action.data,
      };
    }
  }
}

/* services to CRUD, import, export data in table */
export const tableService = {
  /**
   *
   * react hook expose data and event handler for master table service
   * @param: filter: TFilter
   * @param: setFilter: (filter: TFilter) => void
   * @param: handleReloadList: any
   *
   * @return: { handleTableChange, handlePagination }
   *
   * */
  useTable<TFilter extends ModelFilter>(
    filter: TFilter,
    setFilter: (filter: TFilter) => void,
    handleReloadList?: (newfilter?: TFilter) => void
  ) {
    const handleTableChange = useCallback(
      (...[, , sorter]) => {
        let newFilter = { ...filter }; // dont check pagination change because of we customize it
        if (
          sorter.field !== filter.orderBy ||
          sorter.order !== getAntOrderType(filter, sorter.field)
        ) {
          newFilter = {
            ...newFilter,
            orderBy: sorter.field,
            orderType: getOrderType(sorter.order),
          };
        } // check sortOrder and sortDirection
        setFilter({ ...newFilter }); // setFilter
        if (typeof handleReloadList === "function") {
          handleReloadList({ ...newFilter });
        } // handleReloadList
      },
      [filter, setFilter, handleReloadList]
    );

    const handlePagination = useCallback(
      (skip: number, take: number) => {
        setFilter({ ...filter, skip, take });
        if (typeof handleReloadList === "function") {
          handleReloadList({ ...filter, skip, take });
        }
      },
      [filter, setFilter, handleReloadList]
    );

    return {
      handleTableChange,
      handlePagination,
    };
  },

  /**
   *
   * react hook for handle action content table
   * @param: ClassFilter: new () => TFilter
   * @param: ClassContent: new () => T
   * @param: data: T[]
   * @param: setData: (t: T[]) => void
   * @param: dispatchFilter: React.Dispatch<FilterAction<TFilter>>
   * @param: handleInvokeChange?: () => void
   *
   * @return: {
      handleResetTable,
      handlePagination,
      handleChangeCell,
      handleChangeRow,
      handleAddRow,
      handleDeleteRow,
    }
   *
   * */
  useContentTable<T extends Model, TFilter extends ModelFilter>(
    ClassFilter: new () => TFilter,
    ClassContent: new () => T,
    data: T[],
    setData: (t: T[]) => void,
    dispatchFilter: React.Dispatch<FilterAction<TFilter>>,
    handleInvokeChange?: () => void
  ) {
    const handleResetTable = useCallback(() => {
      const newFilter = new ClassFilter();
      dispatchFilter({ type: FilterActionEnum.SET, payload: newFilter });
      if (typeof handleInvokeChange === "function") {
        handleInvokeChange();
      }
    }, [ClassFilter, dispatchFilter, handleInvokeChange]);

    const handlePagination = useCallback(
      (skip: number, take: number) => {
        const newFilter = { ...new ClassFilter(), skip, take };
        dispatchFilter({ type: FilterActionEnum.SET, payload: newFilter });
        if (typeof handleInvokeChange === "function") {
          handleInvokeChange();
        }
      },
      [dispatchFilter, handleInvokeChange, ClassFilter]
    );

    const handleChangeCell = useCallback(
      (
        key: string,
        columnKey: keyof T,
        value: T[keyof T],
        validator?: ValidationField
      ) => {
        const rowIdx = data.findIndex((current) => current.key === key);
        if (rowIdx !== -1) {
          if (validator && validator.isValidator) {
            try {
              const schemaField: yup.InferType<any> = yup.reach(
                validator.schema,
                validator.path
              );
              const result = schemaField.validateSync(value);
              if (result instanceof yup.ValidationError) {
                const errorMessage = result.errors.join(", ");
                data[rowIdx]["errors"] = data[rowIdx]["errors"]
                  ? { ...data[rowIdx]["errors"], [columnKey]: errorMessage }
                  : { [columnKey]: errorMessage };
              } else {
                data[rowIdx][columnKey] = value;
              }
            } catch (err: any) {
              return false;
            }
          } else {
            data[rowIdx][columnKey] = value;
          }
          setData(data);
          if (typeof handleInvokeChange === "function") {
            handleInvokeChange();
          }
        }
      },
      [data, setData, handleInvokeChange]
    );

    const handleChangeRow = useCallback(
      (key: string, value: T, validator?: ValidationField) => {
        const rowIdx = data.findIndex((current) => current.key === key);
        if (rowIdx !== -1) {
          if (validator && validator.isValidator && validator.schema) {
            try {
              const result = validator.schema.validateSync(value);
              if (result instanceof yup.ValidationError) {
                const errors = result.inner.reduce((acc, error) => {
                  return {
                    ...acc,
                    [error.path]: error.errors.join(", "),
                  };
                }, {});
                data[rowIdx]["errors"] = data[rowIdx]["errors"]
                  ? { ...data[rowIdx]["errors"], ...errors }
                  : { ...errors };
              } else {
                data[rowIdx] = value;
              }
            } catch (error: any) {
              return false;
            }
          } else {
            data[rowIdx] = value;
          }
          setData(data);
          if (typeof handleInvokeChange === "function") {
            handleInvokeChange();
          }
        }
      },
      [data, setData, handleInvokeChange]
    );

    const handleAddRow = useCallback(() => {
      data.unshift(new ClassContent());
      setData(data);
      if (typeof handleInvokeChange === "function") {
        handleInvokeChange();
      }
    }, [ClassContent, data, setData, handleInvokeChange]);

    const handleDeleteRow = useCallback(
      (key: string | number) => {
        if (data && data.length > 0) {
          const rowIdx = data.findIndex((current) => current.key === key);
          data.splice(rowIdx, 1);
          setData(data);
        }
      },
      [data, setData]
    );

    const handleInsertRow = useCallback(
      (index: number, row: T) => {
        data.splice(index + 1, 0, row);
        setData(data);
        if (typeof handleInvokeChange === "function") {
          handleInvokeChange();
        }
      },
      [data, handleInvokeChange, setData]
    );

    const handleAddRowWithData = useCallback(
      (newData?: T) => {
        if (newData) {
          data.push(newData);
        } else {
          data.push(new ClassContent());
        }
        setData(data);
        if (typeof handleInvokeChange === "function") {
          handleInvokeChange();
        }
      },
      [ClassContent, data, setData, handleInvokeChange]
    );

    return {
      handleResetTable,
      handlePagination,
      handleChangeCell,
      handleChangeRow,
      handleAddRow,
      handleAddRowWithData,
      handleDeleteRow,
      handleInsertRow,
    };
  },
  useContenModal(handleSource?: () => void) {
    const [{ visible, loadControl }, dispatch] = useReducer<
      Reducer<ModalState, ModalAction>
    >(modalTableReducer, {
      visible: false,
      loadControl: false,
    });

    const handleOpenModal = useCallback(() => {
      dispatch({ type: ModalActionEnum.OPEN_MODAL });
    }, []);

    const handleCloseModal = useCallback(() => {
      dispatch({ type: ModalActionEnum.CLOSE_MODAL });
    }, []);

    const handleSaveModal = useCallback(() => {
      dispatch({ type: ModalActionEnum.CLOSE_MODAL });
      if (typeof handleSource === "function") {
        handleSource(); // trigger reload list
      }
    }, [handleSource]);

    const handleEndControl = useCallback(() => {
      dispatch({ type: ModalActionEnum.END_LOAD_CONTROL });
    }, []);

    const handleSearchModal = useCallback(() => {
      dispatch({ type: ModalActionEnum.INIT_SEARCH });
    }, []);

    return {
      visible,
      loadControl,
      handleEndControl,
      handleOpenModal,
      handleCloseModal,
      handleSaveModal,
      handleSearchModal,
    };
  },
  useContentRowSelection<T extends Model>(
    selectedList: T[],
    setSelectedList: (t: T[]) => void,
    disabled: boolean = false,
    selectionType: RowSelectionType = "checkbox"
  ): {
    rowSelection: TableRowSelection<T>;
    canBulkDelete: boolean;
  } {
    const selectedRowKeys: KeyType[] = useMemo(() => {
      return selectedList.length > 0
        ? selectedList.map((t: T) => (t?.id ? t?.id : t?.key))
        : [];
    }, [selectedList]); // selectedRowKeys accept both string and number for local and server table

    const canBulkDelete = useMemo(
      () => selectedRowKeys.length > 0,
      [selectedRowKeys.length]
    ); // decide when we can enable bulkDelete

    return {
      rowSelection: useMemo(
        () => ({
          preserveSelectedRowKeys: true,
          onSelect: (record: T, selected: boolean) => {
            if (selected) {
              selectedList.push(record);
              setSelectedList([...selectedList]);
            } else {
              setSelectedList(
                selectedList.filter((t: T) => {
                  return t.id ? t.id !== record.id : t.key !== record.key;
                })
              );
            }
          }, // single selection
          onChange: (...[selectedRowKeys, selectedRows]) => {
            if (selectedList?.length === 0) {
              setSelectedList([...selectedRows]);
              return;
            } // if list empty, add all selectedRows to list
            const mapper: Record<any, number> = {}; // create mapper from filter
            selectedRowKeys.forEach((key: string | number) => {
              mapper[key] = 0;
            });
            const mergeList = [...selectedList, ...selectedRows].filter(
              (item) => typeof item !== "undefined"
            ); // merge old list with new selectedRows
            const filterList: any[] = [];
            mergeList.forEach((item) => {
              const itemId =
                typeof item?.id !== "undefined" ? item.id : item.key;
              // eslint-disable-next-line no-prototype-builtins
              if (mapper.hasOwnProperty(itemId) && mapper[itemId] === 0) {
                filterList.push(item);
                mapper[itemId] = mapper[itemId] + 1;
              }
            }); // filter item which its key contained in selectedRowKeys
            setSelectedList([...filterList]);
          }, // multi selection
          getCheckboxProps: () => ({
            disabled,
          }), // pass external control for disabling selection
          type: selectionType, // selection type
          selectedRowKeys, // selectedRowKey
        }),
        [
          disabled,
          selectedList,
          selectedRowKeys,
          selectionType,
          setSelectedList,
        ]
      ),
      canBulkDelete,
    };
  },
  usePagination<TFilter extends ModelFilter>(
    filter: TFilter,
    total: number
  ): PaginationProps {
    return useMemo(
      () => ({
        current: Math.ceil(filter.skip / filter.take) + 1,
        pageSize: filter.take,
        total,
      }),
      [filter.skip, filter.take, total]
    );
  },
  useModalTable<T extends Model, TFilter extends ModelFilter>(
    getList: (filter: TFilter) => Observable<T[]>,
    getCount: (filter: TFilter) => Observable<number>,
    filter: TFilter,
    dispatchFilter?: React.Dispatch<FilterAction<TFilter>>,
    // handleSearch: () => void,
    mapperList?: T[]
  ) {
    // mappingList, mapperList reducer
    const [{ mapperList: selectedList }, dispatch] = useReducer<
      Reducer<ContentTableState<T>, ContentTableAction<T>>
    >(contentTableReducer, {
      mapperList: mapperList ?? [],
    });

    const setSelectedList = useCallback((list: T[]) => {
      dispatch({
        type: ContentTableActionEnum.SET_LIST_SELECTION,
        listName: "mapperList",
        data: list,
      });
    }, []);

    // selectedRowKeys
    const { rowSelection } = this.useContentRowSelection(
      selectedList,
      setSelectedList
    );

    // define setMapperList alternater for setSelectedList
    const setMapperList = useCallback((mapperList: T[]) => {
      dispatch({
        type: ContentTableActionEnum.SET_LIST_SELECTION,
        listName: "mapperList",
        data: mapperList, // update mappingList.Eg: selectedContent[]
      });
    }, []);

    // update mapperList when source changed
    useEffect(() => {
      if (mapperList) {
        setMapperList(mapperList);
      }
    }, [mapperList, setMapperList]);

    // from filter and source we calculate dataSource, total and loadingList
    const { list, count, loadingList } = listService.useList(
      getList,
      getCount,
      filter,
      dispatchFilter
    );

    // calculate pagination
    const pagination: PaginationProps = this.usePagination<TFilter>(
      filter,
      count
    );

    // handleChange page or sorter

    const { handleTableChange, handlePagination } = tableService.useTable(
      filter as any,
      dispatchFilter
    );

    return {
      list,
      count,
      loadingList,
      pagination,
      handleTableChange,
      handlePagination,
      rowSelection,
      selectedList,
      setSelectedList,
    };
  },
};

export function getAntOrderType<T extends Model, TFilter extends ModelFilter>(
  tFilter: TFilter,
  columnName: keyof T
): SortOrder {
  if (tFilter.orderBy === columnName) {
    switch (tFilter.orderType) {
      case "ASC":
        return "ascend";

      case "DESC":
        return "descend";

      default:
        return undefined;
    }
  }
  return undefined;
}

export function getOrderType(sortOrder?: SortOrder) {
  switch (sortOrder) {
    case "ascend":
      return "ASC";

    case "descend":
      return "DESC";

    default:
      return undefined;
  }
}

export function mappingToMapper<T extends Model>(mapperField: string) {
  return (item: T) => item[mapperField];
}

export function filterContentNotInList<T extends Model>(
  list: any[],
  fieldId: string
) {
  return (item: T) => !list.includes(item[fieldId]);
}

export function filterContentInList<T extends Model>(
  list: any[],
  fieldId: string
) {
  return (item: T) => list.includes(item[fieldId]);
}
